import {
    FormReportModel,
    IReportCompetitorsBenchmark,
    ReportAverageRpdRequest,
    ReportCompetitorsBenchmarkRequest,
    ReportIncomingReservationRequest,
    ReportNumberOfDaysOfReservationRequest,
    ReportNumberOfReservationCarGroupsRequest,
    ReportNumberOfReservationsRequest,
    ReportRevenuesRequest,
    ReportUtilizationAndAvailabilityRequest,
    RowsWithValuesArray,
    RowsWithValuesNumber,
} from "../../../api/types/report";
import {UseFormReturn} from "react-hook-form";
import {useReportsRepository} from "../../../api/repositories/ReportRepository";
import dayjs from "dayjs";
import {PRICING_REPORTS} from "../../../constants";

export const useReportUtils = (
    activeStep: number,
    setActiveStep: Function,
    form: UseFormReturn<FormReportModel, any>
) => {
    const {
        postReportIncomingReservation,
        getReportCompetitorsBenchmark,
        postReportAverageRpd,
        postReportRevenues,
        postReportNumberOfDaysOfReservation,
        getReportNumberOfReservationsCarGroups,
        getReportNumberOfReservations,
        getReportUtilizationAndAvailability,
    } = useReportsRepository();

    const triggerAverageRpdLocationFields = async (): Promise<boolean> => {
        const locationsGroupIdsWatcher = form.watch("averageRpd.locationsGroupIds");
        const locationsIdsWatcher = form.watch("averageRpd.locationsIds");

        switch (true) {
            case locationsGroupIdsWatcher?.length === 0 &&
            locationsIdsWatcher?.length === 0: {
                const locationsIdsTriggered = await form.trigger(
                    "averageRpd.locationsIds"
                );
                const locationsGroupIdsTriggered = await form.trigger(
                    "averageRpd.locationsGroupIds"
                );

                return locationsIdsTriggered && locationsGroupIdsTriggered;
            }
            case locationsIdsWatcher?.length > 0: {
                form.resetField("averageRpd.locationsGroupIds");
                return await form.trigger("averageRpd.locationsIds");
            }
            case locationsGroupIdsWatcher?.length > 0: {
                form.resetField("averageRpd.locationsIds");
                return await form.trigger("averageRpd.locationsGroupIds");
            }
            default: {
                return false;
            }
        }
    };

    const triggerFormFields = async () => {
        const type = form.watch("type")?.label;
        switch (activeStep) {
            case 0: {
                const result = await form.trigger("type");
                if (result) {
                    setActiveStep((prevActiveStep: number) => prevActiveStep + 1);
                }
                return result;
            }
            case 1: {
                switch (type) {
                    case PRICING_REPORTS.COMPETITORS_BENCHMARK: {
                        const from = await form.trigger("competitorsBenchmark.from");
                        const rentalPeriod = await form.trigger(
                            "competitorsBenchmark.rentalPeriod"
                        );
                        const locationPickUp = await form.trigger(
                            "competitorsBenchmark.locationPickUp"
                        );
                        const locationDropOf = await form.trigger(
                            "competitorsBenchmark.locationDropOff"
                        );
                        const site = await form.trigger("competitorsBenchmark.site");
                        const dealer = await form.trigger("dealer");
                        const legalEntity = await form.trigger("legalEntity");
                        return (
                            from &&
                            rentalPeriod &&
                            locationPickUp &&
                            locationDropOf &&
                            site &&
                            dealer &&
                            legalEntity
                        );
                    }
                    case PRICING_REPORTS.AVERAGE_RPD: {
                        const startMonthPickUp = await form.trigger(
                            "averageRpd.startMonthPickUp"
                        );
                        const numberOfMonths = await form.trigger(
                            "averageRpd.numberOfMonths"
                        );
                        const dateType = await form.trigger("averageRpd.dateType");
                        const startDate = await form.trigger("averageRpd.startDate");
                        const endDate = await form.trigger("averageRpd.endDate");
                        const locations = await triggerAverageRpdLocationFields();
                        const dealer = await form.trigger("dealer");
                        const legalEntity = await form.trigger("legalEntity");
                        return (
                            startMonthPickUp &&
                            numberOfMonths &&
                            dateType &&
                            locations &&
                            startDate &&
                            endDate &&
                            dealer &&
                            legalEntity
                        );
                    }
                    case PRICING_REPORTS.UTILIZATION_AVAILABILITY: {
                        const location = await form.trigger("utilizationAndAvailability.location");
                        const legalEntity = await form.trigger("legalEntity");
                        return (location && legalEntity)
                    }
                    case PRICING_REPORTS.RESERVATIONS_PER_DAY: {
                        const startMonthPickUp = await form.trigger(
                            "numberOfDaysOfReservations.startMonthPickUp"
                        );
                        const numberOfMonths = await form.trigger(
                            "numberOfDaysOfReservations.numberOfMonths"
                        );
                        const dateType = await form.trigger(
                            "numberOfDaysOfReservations.dateType"
                        );
                        const groupsOfLocations = await form.trigger(
                            "numberOfDaysOfReservations.locationsGroupIds"
                        );
                        const startDate = await form.trigger(
                            "numberOfDaysOfReservations.startDate"
                        );
                        const endDate = await form.trigger(
                            "numberOfDaysOfReservations.endDate"
                        );
                        const dealer = await form.trigger("dealer");
                        const legalEntity = await form.trigger("legalEntity");
                        return (
                            startMonthPickUp &&
                            numberOfMonths &&
                            dateType &&
                            groupsOfLocations &&
                            startDate &&
                            endDate &&
                            dealer &&
                            legalEntity
                        );
                    }
                    case PRICING_REPORTS.RESERVATIONS_BOOKING_DATE_AND_CARGROUPS: {
                        const startDate = await form.trigger(
                            "numberOfReservationsCarGroups.startDate"
                        );
                        const endDate = await form.trigger(
                            "numberOfReservationsCarGroups.endDate"
                        );
                        const vehicleGroups = await form.trigger(
                            "numberOfReservationsCarGroups.carGroups"
                        );
                        const dealer = await form.trigger("dealer");
                        const legalEntity = await form.trigger("legalEntity");
                        return (
                            startDate && endDate && vehicleGroups && dealer && legalEntity
                        );
                    }
                    case PRICING_REPORTS.REVENUES: {
                        const startMonthPickUp = await form.trigger(
                            "revenues.startMonthPickUp"
                        );
                        const numberOfMonths = await form.trigger(
                            "revenues.numberOfMonths"
                        );
                        const dateType = await form.trigger("revenues.dateType");
                        const groupsOfLocations = await form.trigger(
                            "revenues.locationsGroupIds"
                        );
                        const startDate = await form.trigger("revenues.startDate");
                        const endDate = await form.trigger("revenues.endDate");
                        const dealer = await form.trigger("dealer");
                        const legalEntity = await form.trigger("legalEntity");
                        return (
                            startMonthPickUp &&
                            numberOfMonths &&
                            dateType &&
                            groupsOfLocations &&
                            startDate &&
                            endDate &&
                            dealer &&
                            legalEntity
                        );
                    }
                    case PRICING_REPORTS.RESERVATIONS_NUMBER: {
                        const startMonthPickUp = await form.trigger(
                            "numberOfReservations.startMonthPickUp"
                        );
                        const numberOfMonths = await form.trigger(
                            "numberOfReservations.numberOfMonths"
                        );
                        const dateType = await form.trigger(
                            "numberOfReservations.dateType"
                        );
                        const groupsOfLocations = await form.trigger(
                            "numberOfReservations.locationsGroupIds"
                        );
                        const startDate = await form.trigger(
                            "numberOfReservations.startDate"
                        );
                        const endDate = await form.trigger("numberOfReservations.endDate");
                        const dealer = await form.trigger("dealer");
                        const legalEntity = await form.trigger("legalEntity");
                        return (
                            startMonthPickUp &&
                            numberOfMonths &&
                            dateType &&
                            groupsOfLocations &&
                            startDate &&
                            endDate &&
                            dealer &&
                            legalEntity
                        );
                    }
                    case PRICING_REPORTS.RPD_INCOMING_RESERVATIONS: {
                        const dayFromReservationDate = await form.trigger(
                            "incomingReservation.dayFromReservationDate"
                        );
                        const numberOfMonths = await form.trigger(
                            "incomingReservation.numberOfMonths"
                        );
                        const groupsOfLocations = await form.trigger(
                            "incomingReservation.locationsGroupIds"
                        );
                        const startMonthPickUp = await form.trigger(
                            "incomingReservation.startMonthPickUp"
                        );
                        const dealer = await form.trigger("dealer");
                        const legalEntity = await form.trigger("legalEntity");
                        return (
                            startMonthPickUp &&
                            numberOfMonths &&
                            dayFromReservationDate &&
                            groupsOfLocations &&
                            dealer &&
                            legalEntity
                        );
                    }
                    default: {
                        break;
                    }
                }
                break;
            }
            default:
                break;
        }
    };

    const createPostRequest = async (everyLegalEntitiesWithoutAll: string[], dealerFilteredOptions: string[]) => {
        try {
            const values = form.getValues();
            const type = form.watch("type")?.label;

            switch (type) {
                case PRICING_REPORTS.RPD_INCOMING_RESERVATIONS: {
                    const report: ReportIncomingReservationRequest = {
                        numberOfMonths: values.incomingReservation.numberOfMonths,
                        startMonth: values.incomingReservation.startMonthPickUp,
                        year: values.incomingReservation.year,
                        locationsGroupIds: [
                            values.incomingReservation.locationsGroupIds?.code as number,
                        ],
                        dayFromReservationDate:
                        values.incomingReservation.dayFromReservationDate,
                        commercialSegment: values.incomingReservation.smCommercialSegmentId
                            ?.code as number,
                        dealers: dealerFilteredOptions.length === 1 ? dealerFilteredOptions : [values.dealer?.code as string],
                        legalEntities: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll : [values.legalEntity?.code as string]
                    };
                    return await postReportIncomingReservation(report);
                }
                case PRICING_REPORTS.AVERAGE_RPD: {
                    const report: ReportAverageRpdRequest = {
                        numberOfMonths: values.averageRpd.numberOfMonths as number,
                        startMonth: values.averageRpd.startMonthPickUp as string,
                        year: values.averageRpd.year,
                        locationsGroupIds: values.averageRpd.locationsGroupIds?.map(
                            (l) => l.code as number
                        ),
                        dateType: values.averageRpd.dateType?.code as string,
                        locationCodes: values.averageRpd.locationsIds?.map(
                            (l) => l.code as string
                        ),
                        commercialSegment: values.averageRpd.smCommercialSegmentId
                            ?.code as number,
                        startDate: values.averageRpd.startDate as unknown as string,
                        endDate: values.averageRpd.endDate
                            ? (values.averageRpd.endDate as unknown as string)
                            : null,
                        dealers: dealerFilteredOptions.length === 1 ? dealerFilteredOptions : [values.dealer?.code as string],
                        legalEntities: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll : [values.legalEntity?.code as string]
                    };
                    return await postReportAverageRpd(report);
                }
                case PRICING_REPORTS.REVENUES: {
                    const report: ReportRevenuesRequest = {
                        numberOfMonths: values.revenues.numberOfMonths as number,
                        startMonth: values.revenues.startMonthPickUp as string,
                        year: values.revenues.year,
                        locationsGroupIds: values.revenues.locationsGroupIds?.map(
                            (l) => l.code as number
                        ),
                        dateType: values.revenues.dateType?.code as string,
                        commercialSegment: values.revenues.smCommercialSegmentId
                            ?.code as number,
                        startDate: values.revenues.startDate as unknown as string,
                        endDate: values.revenues.endDate
                            ? (values.revenues.endDate as unknown as string)
                            : null,
                        dealers: dealerFilteredOptions.length === 1 ? dealerFilteredOptions : [values.dealer?.code as string],
                        legalEntities: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll : [values.legalEntity?.code as string]
                    };
                    return await postReportRevenues(report);
                }
                case PRICING_REPORTS.RESERVATIONS_PER_DAY: {
                    const report: ReportNumberOfDaysOfReservationRequest = {
                        numberOfMonths: values.numberOfDaysOfReservations
                            .numberOfMonths as number,
                        startMonth: values.numberOfDaysOfReservations
                            .startMonthPickUp as string,
                        year: values.numberOfDaysOfReservations.year,
                        locationsGroupIds:
                            values.numberOfDaysOfReservations.locationsGroupIds?.map(
                                (l) => l.code as number
                            ),
                        dateType: values.numberOfDaysOfReservations.dateType
                            ?.code as string,
                        startDate: values.numberOfDaysOfReservations
                            .startDate as unknown as string,
                        endDate: values.numberOfDaysOfReservations.endDate
                            ? (values.numberOfDaysOfReservations.endDate as unknown as string)
                            : null,
                        commercialSegment: values.numberOfDaysOfReservations
                            .smCommercialSegmentId?.code as string,
                        dealers: dealerFilteredOptions.length === 1 ? dealerFilteredOptions : [values.dealer?.code as string],
                        legalEntities: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll : [values.legalEntity?.code as string]
                    };
                    return await postReportNumberOfDaysOfReservation(report);
                }
                case PRICING_REPORTS.COMPETITORS_BENCHMARK: {
                    const report: ReportCompetitorsBenchmarkRequest = {
                        dateFrom: dayjs(values.competitorsBenchmark.from).format(
                            "YYYY-MM-DD"
                        ),
                        lor: values.competitorsBenchmark.rentalPeriod,
                        pickupOffice: values.competitorsBenchmark.locationPickUp as string,
                        dropoffOffice: values.competitorsBenchmark
                            .locationDropOff as string,
                        site: values.competitorsBenchmark.site,
                    };
                    // COMPETITORS_BENCHMARK_REPORTS;
                    const reportFromBe = await getReportCompetitorsBenchmark(report);
                    let reports: IReportCompetitorsBenchmark[] = [];
                    reportFromBe.forEach((report) => {
                        const currentReportFe: IReportCompetitorsBenchmark = {
                            searchDate: report.searchDate,
                            office: report.office,
                            site: report.site,
                            date: report.date,
                            days: report.days,
                            columns: report.columns.reduce((acc: string[], el: string) => {
                                if (!acc.find((currentEl) => currentEl === el)) acc.push(el);
                                return acc;
                            }, []),
                            rows: report.rows.reduce(
                                (acc: RowsWithValuesArray[], el: RowsWithValuesNumber) => {
                                    const index = acc.findIndex(
                                        (currentEl: RowsWithValuesArray) =>
                                            currentEl.category === el.category
                                    );
                                    if (index > -1) {
                                        acc[index].values.push(el.values);
                                    } else {
                                        acc.push({
                                            category: el.category,
                                            values: [el.values],
                                        });
                                    }
                                    return acc;
                                },
                                []
                            ),
                        };
                        reports.push(currentReportFe);
                    });
                    return reports;
                }
                case PRICING_REPORTS.RESERVATIONS_BOOKING_DATE_AND_CARGROUPS: {
                    const report: ReportNumberOfReservationCarGroupsRequest = {
                        startDate: dayjs(
                            values.numberOfReservationsCarGroups.startDate
                        ).toISOString(),
                        endDate: dayjs(
                            values.numberOfReservationsCarGroups.endDate
                        ).toISOString(),
                        carGroups: values.numberOfReservationsCarGroups.carGroups,
                        commercialSegment: values.numberOfReservationsCarGroups
                            .smCommercialSegmentId?.code as string,
                        dealers: dealerFilteredOptions.length === 1 ? dealerFilteredOptions : [values.dealer?.code as string],
                        legalEntities: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll : [values.legalEntity?.code as string]
                    };
                    return await getReportNumberOfReservationsCarGroups(report);
                }
                case PRICING_REPORTS.RESERVATIONS_NUMBER: {
                    const report: ReportNumberOfReservationsRequest = {
                        numberOfMonths: values.numberOfReservations.numberOfMonths,
                        startMonth: values.numberOfReservations.startMonthPickUp,
                        year: values.numberOfReservations.year,
                        locationsGroupIds:
                            values.numberOfReservations.locationsGroupIds?.map(
                                (l) => l.code as number
                            ),
                        dateType: values.numberOfReservations.dateType?.code as string,
                        startDate: dayjs(
                            values.numberOfReservations.startDate
                        ).toISOString(),
                        endDate: values.numberOfReservations.endDate
                            ? dayjs(values.numberOfReservations.endDate).toISOString()
                            : null,
                        commercialSegment: values.numberOfReservations.smCommercialSegmentId
                            ?.code as string,
                        dealers: dealerFilteredOptions.length === 1 ? dealerFilteredOptions : [values.dealer?.code as string],
                        legalEntities: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll : [values.legalEntity?.code as string]
                    };
                    return await getReportNumberOfReservations(report);
                }
                case PRICING_REPORTS.UTILIZATION_AVAILABILITY: {
                    const report: ReportUtilizationAndAvailabilityRequest = {
                        location: values.utilizationAndAvailability.location
                            ?.code as string,
                        currentMonthSelected:
                        values.utilizationAndAvailability.currentMonthSelected === undefined ? false : values.utilizationAndAvailability.currentMonthSelected,
                        legalEntity: everyLegalEntitiesWithoutAll.length === 1 ? everyLegalEntitiesWithoutAll[0] : values.legalEntity?.code as string
                    };
                    return await getReportUtilizationAndAvailability(report);
                }
                default:
                    break;
            }
        } catch (e) {
            return null;
        }
    };

    return {
        triggerFormFields,
        createPostRequest,
    };
};
